
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { singularityStore, globalStore, configStore } from "@/store/store";
import {
  BorderRadius,
  BORDER_RADIUS_VALUES,
  ButtonSize
} from "@/types/singularity/brand";
import { CONTROL_PADDING } from "@/lib/singularity/brand/buttons-forms";
import IconButtonShuffle from "../../components/IconButtonShuffle.vue";
import Icon from "@/components/UI/Icon.vue";
import TogglePanel from "../../components/TogglePanel.vue";
import { StyleValue } from "vue/types/jsx";
import {
  updateGlobalButtonStyles,
  updateGlobalFormStyles
} from "@/lib/singularity/brand/buttons-forms";
import { logEvent } from "@/util/tracking";
import BrandToggleButtonFormRadius from "./BrandToggleButtonFormRadius.vue";
import BrandToggleButtonSize from "./BrandToggleButtonSize.vue";
import BrandSampleUI from "./BrandSampleUI.vue";
import { SampleUICssStyles } from "@/types/singularity/brand";
import { breakdanceUndo } from "@/lib/undo/undo";

@Component({
  components: {
    IconButtonShuffle,
    Icon,
    TogglePanel,
    BrandToggleButtonFormRadius,
    BrandToggleButtonSize,
    BrandSampleUI
  }
})
export default class BrandManageColors extends Vue {
  get brand() {
    return singularityStore.brand;
  }

  get bodyFont() {
    const bodyFont = globalStore.globalSettings.settings.typography.body_font;
    return configStore.fonts.find(font => font.slug === bodyFont);
  }

  radiuses: BorderRadius[] = ["none", "sm", "md", "full"];

  buttonSizes: ButtonSize[] = ["sm", "default", "md", "lg"];

  get isPanelOpen() {
    return singularityStore.brandPanelState?.openedPanel === "ui";
  }

  updateRadiuses(radius: BorderRadius) {
    singularityStore.setInputRadius({
      inputRadius: radius
    });
    updateGlobalButtonStyles({ radius: this.inputBorderRadius });
    updateGlobalFormStyles({ radius: this.inputBorderRadius });

    logEvent("FutureLayer Brand: Input Radius Updated", {
      inputRadius: this.inputBorderRadius,
      buttonSize: this.inputButtonSize
    });
  }

  updateButtonSizes(size: ButtonSize) {
    singularityStore.setButtonSize({
      buttonSize: size
    });
    updateGlobalButtonStyles({ radius: this.inputBorderRadius });

    logEvent("FutureLayer Brand: Button Size Updated", {
      inputRadius: this.inputBorderRadius,
      buttonSize: this.inputButtonSize
    });
  }

  get inputBorderRadius() {
    return singularityStore.brand.ui.inputRadius;
  }

  get inputButtonSize() {
    return singularityStore.brand.ui.buttonSize;
  }

  get sampleUIStyles() {
    if (!this.brand.color.neutral) {
      return {};
    }
    return {
      backgroundColor: this.brand.color.primary?.scale[100].value?.toString()
    };
  }

  get stylesPrimary() {
    if (!this.brand.color.primary) {
      return {} as StyleValue;
    }
    return {
      backgroundColor: this.brand.color.primary.base.value?.toString(),
      color: this.brand.color.primary.text.value?.toString(),
      borderRadius: BORDER_RADIUS_VALUES[this.inputBorderRadius]?.style,
      padding: CONTROL_PADDING[this.inputButtonSize]?.string
    } as StyleValue;
  }

  get stylesSecondary() {
    if (!this.brand.color.neutral) {
      return {} as StyleValue;
    }
    console.log(CONTROL_PADDING[this.inputButtonSize]?.string);
    return {
      backgroundColor: "transparent",
      color: this.brand.color.neutral.base.value?.toString(),
      borderColor: this.brand.color.neutral.scale[300].value?.toString(),
      borderRadius: BORDER_RADIUS_VALUES[this.inputBorderRadius]?.style,
      padding: CONTROL_PADDING[this.inputButtonSize]?.string
    } as StyleValue;
  }

  get stylesLabel() {
    if (!this.brand.color.neutral) {
      return {} as StyleValue;
    }
    return {
      color: this.brand.color.neutral.base.value?.toString()
      // fontFamily: this.bodyFont?.cssName || "sans-serif"
    } as StyleValue;
  }

  get stylesForms() {
    if (!this.brand.color.neutral) {
      return {} as StyleValue;
    }
    return {
      borderColor: this.brand.color.neutral.scale[200].value?.toString(),
      borderRadius: BORDER_RADIUS_VALUES[this.inputBorderRadius]?.style,
      color: this.brand.color.neutral.scale[300].value?.toString()
    } as StyleValue;
  }

  get cssStyles(): SampleUICssStyles {
    return {
      sampleUI: this.sampleUIStyles,
      label: this.stylesLabel,
      forms: this.stylesForms,
      primary: this.stylesPrimary,
      secondary: this.stylesSecondary
    };
  }

  randomUniqueRadius() {
    const currentRadius = this.inputBorderRadius;
    let newRadius = currentRadius;

    while (newRadius === currentRadius) {
      newRadius =
        this.radiuses[Math.floor(Math.random() * this.radiuses.length)];
    }

    return newRadius;
  }

  randomUniqueButtonSize() {
    const currentSize = this.inputButtonSize;
    let newSize = currentSize;

    while (newSize === currentSize) {
      newSize =
        this.buttonSizes[Math.floor(Math.random() * this.buttonSizes.length)];
    }

    return newSize;
  }

  shuffle() {
    const randomRadius = this.randomUniqueRadius();
    const randomButtonSize = this.randomUniqueButtonSize();

    breakdanceUndo.transaction(() => {
      this.updateRadiuses(randomRadius);
      this.updateButtonSizes(randomButtonSize);
    }, "Shuffled buttons & forms");

    logEvent("FutureLayer Brand: Buttons & Forms Shuffled", {
      inputRadius: this.inputBorderRadius,
      buttonSize: this.inputButtonSize
    });
  }
}
